import { DEFAULT_INITIAL_BILLING_OPTIONS, MONTHS } from '../constants/print-store'
import { Product, ShippingOption } from '@dtx-company/flow-codegen/src/code/generated.types'
import {
  SelectedBillingOptionsProps,
  UsePrintProductDetailsProps,
  UsePrintProductDetailsReturnType,
  UseProductDeliveryDateRangeProps
} from '../types/print-store.types'
import { getPriceForQuantity, getProductEstimatedDeliveryDate } from '../utils/print-store'
import { useCallback, useEffect, useState } from 'react'
import { useIsMounted } from '@dtx-company/true-common/src/hooks/useIsMounted'

export function usePrintProductDetails({
  productDetails
}: UsePrintProductDetailsProps): UsePrintProductDetailsReturnType {
  const [initialBillingOptions, setInitialBillingOptions] = useState<SelectedBillingOptionsProps>()
  const isMounted = useIsMounted()

  useEffect(() => {
    if (isMounted() && productDetails) {
      const getInitialBillingOptions = (): void => {
        const orderQuantity = productDetails?.product?.priceModel?.sortedTiers?.[0]?.quantity ?? 0
        const orderQuantityPrice = getPriceForQuantity(productDetails.product, orderQuantity)
        setInitialBillingOptions(() => {
          return {
            ...DEFAULT_INITIAL_BILLING_OPTIONS,
            quantity: orderQuantity,
            shippingOptionId:
              productDetails.shippingOptions[0].id ||
              DEFAULT_INITIAL_BILLING_OPTIONS.shippingOptionId,
            shippingCost:
              productDetails.shippingOptions[0].price ||
              DEFAULT_INITIAL_BILLING_OPTIONS.shippingCost,
            unitAmount: orderQuantityPrice.unitAmount,
            itemsSubTotal: orderQuantityPrice.price
          }
        })
      }
      getInitialBillingOptions()
    }
  }, [productDetails, isMounted])

  return { initialBillingOptions }
}

export function useProductDeliveryDateRange(): UseProductDeliveryDateRangeProps {
  const getEstimatedDeliveryDates = useCallback(
    (
      deliveryDaysRangeMin: ShippingOption['deliveryDaysRangeMin'],
      deliveryDaysRangeMax: ShippingOption['deliveryDaysRangeMax'],
      basicProcessingTime: Product['handlingTimeBusinessDays'],
      nonWorkingDays?: ShippingOption['excludedDeliveryDaysOfWeek']
    ) => {
      return getProductEstimatedDeliveryDate(
        deliveryDaysRangeMin,
        deliveryDaysRangeMax,
        basicProcessingTime,
        nonWorkingDays
      )
    },
    []
  )

  const getEstimatedDeliveryDatesFromUTC = useCallback(
    (
      estimatedDeliveryDateMin: ShippingOption['estimatedDeliveryDateMin'],
      estimatedDeliveryDateMax: ShippingOption['estimatedDeliveryDateMax']
    ): string => {
      if (!estimatedDeliveryDateMin || !estimatedDeliveryDateMax) return '-'
      const estimatedDeliveryLocalDateMin = new Date(estimatedDeliveryDateMin)
      const estimatedDeliveryLocalDateMax = new Date(estimatedDeliveryDateMax)
      return `${
        MONTHS[estimatedDeliveryLocalDateMin.getMonth()]
      } ${estimatedDeliveryLocalDateMin.getDate()} - 
      ${
        MONTHS[estimatedDeliveryLocalDateMax.getMonth()]
      } ${estimatedDeliveryLocalDateMax.getDate()}`
    },
    []
  )

  return { getEstimatedDeliveryDatesFromUTC, getEstimatedDeliveryDates }
}
